import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import { Link, Route, Switch, useHistory, useParams } from 'react-router-dom'
import CompanyProfile from 'pages/company/CompanyProfile'
import PriceControlModal from 'pages/company/modals/PriceControlModal'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import notify from 'components/atoms/Notify'
import { getResourceData } from 'lib/utils/resource'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

const AddCompany = ({ readOnly, currentUserId }) => {
  const { t } = useTranslation()
  const history = useHistory()

  const [isEdit, setIsEdit] = useState(null)
  const [companyData, setCompanyData] = useState({})

  const [loading, setLoading] = useState(false)
  const [priceControlOpen, setPriceControlOpen] = useState(false)

  const id = useParams()?.id

  useEffect(() => {
    if (id) {
      setIsEdit(true)
      fetchCompany(id)
    }
  }, [id])

  const fetchCompany = async () => {
    setLoading(true)
    const data = getResourceData(await apiGet(`company/${id}`))
    setLoading(false)

    if (!data) {
      history.push('/companies')
      return
    }

    setCompanyData(data)
  }

  const activateCompany = async () => {
    const res = await apiPut(`company/status/${id}`, { isActive: true })
    if (res?.success) {
      notify.success(t('companies.messages.companyStatusUpdated'))
      fetchCompany()
    }
  }

  return (
    <Layout
      headerRight={
        <div className="mt-4 flex sm:mt-0 sm:ml-4 gap-2">
          {isEdit && (
            <button
              onClick={() => setPriceControlOpen(true)}
              type="button"
              className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-1"
            >
              {t('priceControlModal.title')}
            </button>
          )}
          <button
            onClick={() => {
              history.push('/companies')
            }}
            type="button"
            className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:order-2 sm:ml-3"
          >
            Go Back
          </button>
        </div>
      }
      title={isEdit ? 'Edit Company' : 'Add New Company'}
      breadcrumbs={[
        { name: 'Company List', pathname: '/companies' },
        { name: isEdit ? 'User Details' : 'Add New Users', pathname: '/add/company', current: true },
      ]}
    >
      <PriceControlModal open={priceControlOpen} onClose={() => setPriceControlOpen(false)} companyId={id} />
      {companyData?.needsReview && (
        <div className="m-4 flex flex-col gap-3 rounded-lg border border-yellow-300 bg-yellow-50 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-yellow-900">
            <p className="font-semibold">{t('companySignup.review.badge')}</p>
            <p>{t('companySignup.review.adminBody')}</p>
            {companyData?.crmLead && (
              <Link to={`/crm/leads/${companyData.crmLead}`} className="font-medium underline">
                {t('companySignup.review.viewSignup')}
              </Link>
            )}
          </div>
          {!readOnly && (
            <button type="button" onClick={activateCompany} className="shrink-0 rounded-md bg-dark-600 px-4 py-2 text-sm text-white hover:bg-dark-700">
              {t('companySignup.review.activate')}
            </button>
          )}
        </div>
      )}
      <div className="flex min-w-0 w-full overflow-visible">
        <Switch>
          <Route key={id} path="/add/company" component={() => <CompanyProfile id={id} isEdit={isEdit} />} />
          <Route
            key={id}
            path="/company/edit/:id/company-profile"
            component={() => <CompanyProfile readOnly={readOnly} refetch={fetchCompany} companyData={companyData} loading={loading} id={id} isEdit={isEdit} />}
          />
        </Switch>
      </div>
    </Layout>
  )
}

export default AddCompany
